import React, { useState, useEffect } from "react";
import { db } from "../../services/firebase";
import {
	collection,
	addDoc,
	onSnapshot,
	query,
	orderBy,
} from "firebase/firestore";
import "../../styles/modern-landing.css";

const SIZES = ["XS", "S", "M", "L", "XL", "2XL", "One Size"];

const MerchOrderForm = () => {
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		notes: "",
	});

	const [merchProducts, setMerchProducts] = useState([]);
	const [items, setItems] = useState([]);
	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [loadingProducts, setLoadingProducts] = useState(true);

	// Load products from Firestore
	useEffect(() => {
		const q = query(
			collection(db, "merchProducts"),
			orderBy("createdAt", "desc"),
		);
		const unsubscribe = onSnapshot(q, (snapshot) => {
			const productsData = snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
			setMerchProducts(productsData);
			setItems(
				productsData.map((product) => ({
					...product,
					size: "",
					quantity: 0,
				})),
			);
			setLoadingProducts(false);
		});
		return () => unsubscribe();
	}, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const updateItem = (index, field, value) => {
		setItems((prev) =>
			prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
		);
	};

	const getOrderTotal = () => {
		return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
	};

	const getOrderedItems = () => {
		return items.filter((item) => item.quantity > 0 && item.size);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const orderedItems = getOrderedItems();
		if (orderedItems.length === 0) {
			alert("Please select at least one item with a size and quantity.");
			return;
		}

		setSubmitting(true);
		try {
			await addDoc(collection(db, "merchOrders"), {
				firstName: formData.firstName,
				lastName: formData.lastName,
				email: formData.email,
				phone: formData.phone || "",
				notes: formData.notes || "",
				items: orderedItems.map((item) => ({
					id: item.id,
					name: item.name,
					size: item.size,
					quantity: item.quantity,
					price: item.price,
				})),
				total: getOrderTotal(),
				status: "pending",
				createdAt: new Date().toISOString(),
			});
			setSubmitted(true);
		} catch (error) {
			console.error("Order submission error:", error);
			if (error.code === "permission-denied") {
				alert(
					"Order failed: Firestore rules may need deploying. Please ask your admin to run 'firebase deploy --only firestore:rules'.",
				);
			} else {
				alert(
					"Something went wrong submitting your order. Please try again. (" +
						error.message +
						")",
				);
			}
		} finally {
			setSubmitting(false);
		}
	};

	if (loadingProducts) {
		return (
			<div className="merch-page">
				<div className="merch-page-header">
					<img
						src={process.env.PUBLIC_URL + "/EU59_LogoWHTAsset 5@4x.png"}
						alt="Urban 59 Logo"
						className="merch-page-logo"
					/>
				</div>
				<section className="contact-section">
					<div className="container text-center py-5">
						<div className="spinner-border text-primary" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
					</div>
				</section>
			</div>
		);
	}

	if (!loadingProducts && merchProducts.length === 0) {
		return (
			<div className="merch-page">
				<div className="merch-page-header">
					<img
						src={process.env.PUBLIC_URL + "/EU59_LogoWHTAsset 5@4x.png"}
						alt="Urban 59 Logo"
						className="merch-page-logo"
					/>
				</div>
				<section className="contact-section">
					<div className="container">
						<div className="row justify-content-center">
							<div className="col-lg-8">
								<div
									className="contact-form-wrapper"
									style={{ textAlign: "center", padding: "4rem 3rem" }}
								>
									<h2 className="section-title" style={{ color: "#1a1a1a" }}>
										Coming <span className="highlight">Soon</span>
									</h2>
									<p
										style={{ fontSize: "1.1rem", color: "rgba(26,26,26,0.6)" }}
									>
										Our merchandise store is being updated. Check back soon!
									</p>
								</div>
							</div>
						</div>
					</div>
				</section>
			</div>
		);
	}

	if (submitted) {
		return (
			<div className="merch-page">
				<div className="merch-page-header">
					<img
						src={process.env.PUBLIC_URL + "/EU59_LogoWHTAsset 5@4x.png"}
						alt="Urban 59 Logo"
						className="merch-page-logo"
					/>
				</div>
				<section className="contact-section">
					<div className="container">
						<div className="row justify-content-center">
							<div className="col-lg-8">
								<div
									className="contact-form-wrapper"
									style={{ textAlign: "center", padding: "4rem 3rem" }}
								>
									<h2 className="section-title" style={{ color: "#1a1a1a" }}>
										Order <span className="highlight">Submitted</span>
									</h2>
									<p
										style={{
											fontSize: "1.1rem",
											color: "rgba(26,26,26,0.6)",
											marginBottom: "2rem",
										}}
									>
										Thanks {formData.firstName}! Your merchandise order has been
										received. This is a pre-order, we'll be in touch with
										payment details shortly.
									</p>
									<p
										style={{
											fontSize: "1.5rem",
											fontWeight: "700",
											color: "#d15a36",
										}}
									>
										Order Total: £{getOrderTotal().toFixed(2)}
									</p>
								</div>
							</div>
						</div>
					</div>
				</section>
			</div>
		);
	}

	return (
		<div className="merch-page">
			<div className="merch-page-header">
				<img
					src={process.env.PUBLIC_URL + "/EU59_LogoWHTAsset 5@4x.png"}
					alt="Urban 59 Logo"
					className="merch-page-logo"
				/>
			</div>
			<section className="contact-section">
				<div className="container">
					<div className="row justify-content-center">
						<div className="col-lg-10">
							<div className="contact-form-wrapper">
								<p className="section-label">Merchandise</p>
								<h2 className="section-title" style={{ color: "#1a1a1a" }}>
									Order Your <span className="highlight">Merch</span>
								</h2>
								<p className="contact-subtitle">
									Select your items, sizes and quantities below to pre-order.
									Collection will be from Urban 59.
								</p>

								<form onSubmit={handleSubmit} className="contact-form">
									{/* Personal Details */}
									<div className="form-section">
										<h3 className="form-section-title">Your Details</h3>
										<div className="row">
											<div className="col-md-6">
												<div className="form-group">
													<label htmlFor="firstName">
														First Name{" "}
														<span className="required">(required)</span>
													</label>
													<input
														type="text"
														id="firstName"
														name="firstName"
														value={formData.firstName}
														onChange={handleChange}
														required
														className="form-control"
													/>
												</div>
											</div>
											<div className="col-md-6">
												<div className="form-group">
													<label htmlFor="lastName">
														Last Name{" "}
														<span className="required">(required)</span>
													</label>
													<input
														type="text"
														id="lastName"
														name="lastName"
														value={formData.lastName}
														onChange={handleChange}
														required
														className="form-control"
													/>
												</div>
											</div>
										</div>
									</div>

									<div className="form-group">
										<label htmlFor="email">
											Email <span className="required">(required)</span>
										</label>
										<input
											type="email"
											id="email"
											name="email"
											value={formData.email}
											onChange={handleChange}
											required
											className="form-control"
										/>
									</div>

									{/* <div className="form-group">
										<label htmlFor="phone">
											Phone <span className="required">(required)</span>
										</label>
										<input
											type="tel"
											id="phone"
											name="phone"
											value={formData.phone}
											onChange={handleChange}
											required
											className="form-control"
										/>
									</div> */}

									{/* Merch Items */}
									<div className="form-section">
										<h3 className="form-section-title">Select Your Items</h3>
										<div className="merch-items-grid">
											{items.map((item, index) => (
												<div key={item.id} className="merch-item-card">
													<div className="merch-item-image">
														<img src={item.imageUrl} alt={item.name} />
													</div>
													<div className="merch-item-details">
														<h4 className="merch-item-name">{item.name}</h4>
														{item.description && (
															<p
																className="merch-item-description"
																style={{
																	fontSize: "0.85rem",
																	color: "rgba(26,26,26,0.6)",
																	marginBottom: "0.5rem",
																}}
															>
																{item.description}
															</p>
														)}
														<p className="merch-item-price">
															£{item.price.toFixed(2)}
														</p>
														<div className="merch-item-options">
															<select
																value={item.size}
																onChange={(e) =>
																	updateItem(index, "size", e.target.value)
																}
																className="form-control merch-select"
																aria-label={`Size for ${item.name}`}
															>
																<option value="">Size</option>
																{(item.sizes || SIZES).map((s) => (
																	<option key={s} value={s}>
																		{s}
																	</option>
																))}
															</select>
															<select
																value={item.quantity}
																onChange={(e) =>
																	updateItem(
																		index,
																		"quantity",
																		parseInt(e.target.value),
																	)
																}
																className="form-control merch-select"
																aria-label={`Quantity for ${item.name}`}
															>
																{[0, 1, 2, 3, 4, 5].map((q) => (
																	<option key={q} value={q}>
																		{q === 0 ? "Qty" : q}
																	</option>
																))}
															</select>
														</div>
													</div>
												</div>
											))}
										</div>
									</div>

									{/* Order Summary */}
									{getOrderedItems().length > 0 && (
										<div className="merch-order-summary">
											<h3 className="form-section-title">Order Summary</h3>
											{getOrderedItems().map((item) => (
												<div key={item.id} className="merch-summary-row">
													<span>
														{item.name} ({item.size}) x{item.quantity}
													</span>
													<span>
														£{(item.price * item.quantity).toFixed(2)}
													</span>
												</div>
											))}
											<div className="merch-summary-total">
												<span>Total</span>
												<span>£{getOrderTotal().toFixed(2)}</span>
											</div>
										</div>
									)}

									{/* <div className="form-group">
										<label htmlFor="notes">Any additional notes?</label>
										<textarea
											id="notes"
											name="notes"
											value={formData.notes}
											onChange={handleChange}
											rows="3"
											className="form-control"
											placeholder="e.g. collection preferences"
										/>
									</div> */}

									<button
										type="submit"
										className="btn-submit"
										disabled={submitting}
									>
										{submitting ? "Submitting..." : "Place Order"}
									</button>
								</form>
							</div>
						</div>
					</div>
				</div>
			</section>
		</div>
	);
};

export default MerchOrderForm;
